import { h, Component, Host, Element, Watch, State, Prop, Method } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../../common/colorable-component";
import {
  THIRD_LEVEL_NAVIGATION_ITEM_SCHEME_CONFIG,
  THIRD_LEVEL_NAVIGATION_ITEM_SCHEME_MAP
} from "./constants/third-level-navigation-item-constants";
import {
  ThirdLevelNavigationItemSchemeDefinition,
  ThirdLevelNavigationSchemeName
} from "./constants/third-level-navigation-item-types";
import pfx from "../../../utils/style-class-prefix-helper";
import { LinkTarget } from "../../link/constants/link-types";
import { TARGET_SELF } from "../../link/constants/link-constants";
import { ThirdLevelNavigationItemSchemeConfiguration } from "../../../constants/colors";

@Component({
  tag: "eon-ui-third-level-navigation-item",
  styleUrl: "third-level-navigation-item.scss",
  shadow: true
})
export class ThirdLevelNavigationItem
  implements ColorableComponent<ThirdLevelNavigationItemSchemeConfiguration<ThirdLevelNavigationItemSchemeDefinition>> {
  link: HTMLElement;

  schemeConfig: ThirdLevelNavigationItemSchemeConfiguration<ThirdLevelNavigationItemSchemeDefinition> = THIRD_LEVEL_NAVIGATION_ITEM_SCHEME_CONFIG;
  schemeMap = THIRD_LEVEL_NAVIGATION_ITEM_SCHEME_MAP;

  @State() activeScheme: ThirdLevelNavigationItemSchemeDefinition;

  @Element() hostElement: HTMLEonUiThirdLevelNavigationItemElement;

  /**
   * Method to be able to give focus to the <a> element from outside. Like this
   * third-level-navigation parent component can handle keyboard navigation.
   */
  @Method()
  async focusInnerLink() {
    this.link.focus();
  }

  /**
   * Method to be able to click the <a> element from outside. Like this
   * third-level-navigation parent component can handle keyboard navigation.
   */
  @Method()
  async clickInnerLink() {
    this.link.click();
  }

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop() scheme: ThirdLevelNavigationSchemeName;

  /**
   * Whether or not this navigation item points to the current page's location.
   */
  @Prop() active: boolean = false;

  /**
   * URL of the link pointed at by this navigation item.
   */
  @Prop() href: string = "";

  /**
   * indicates if the link is the first Navigation Item, it is set by parent component "third-level-navigation"
   */
  @Prop({ mutable: true, reflect: true }) firstNavItem: boolean;

  /**
   * Label of the link.
   */
  @Prop() text: string = "";

  /**
   * Target of the link
   */
  @Prop() target: LinkTarget = TARGET_SELF;

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  privateHandleHostFocus() {
    this.link.focus();
  }

  render() {
    return (
      <Host onFocus={this.privateHandleHostFocus.bind(this)} role="gridcell">
        <a
          class={pfx({
            link: true,
            firstNavItem: this.firstNavItem
          })}
          href={this.href}
          ref={(ref) => {
            this.link = ref;
          }}
          title={this.hostElement.getAttribute("title")}
          target={this.target}
        >
          {this.text}
        </a>
      </Host>
    );
  }
}
